import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState, useMemo } from 'react';
import { Popover, usePopover } from '@awaze/design-system.components.popover';
import { Input, Label } from '@awaze/design-system.components.input';
import { DIRECTION_UP, DIRECTION_DOWN, DIRECTION_LEFT, DIRECTION_RIGHT, ENTER_KEY, } from '@awaze/design-system.utils.react';
import { AutocompleteResults } from './autocomplete-results';
import { elementKeyPressHandler } from './autocomplete.utils';
import { StyledClosebutton, StyledContainer, InnerPopoverStyled, } from './autocomplete.styles';
import { Close } from './icon';
/**
 * A component allowing users to see search suggestions based on text input.
 *
 * It can be powered by static data or an asynchronous call to fetch data.
 */
export const Autocomplete = ({ handleOnChange, resultsHandlerCallback, onItemClickCallback, onInputClearCallback, setNoResult, value = '', name = 'autocomplete', children, openOnInputFocus = false, highlightOnFocus = false, highLightAndFocusOnInit = false, offset, translations, label, placement = 'bottom-start', icon, inlineResults = false, minimumInput = 3, popoverWidth, boundaryElement = null, closingPopoverCallback = null, persistPopoverOnNavigation = false, useLegacyDestinationSelector = true, isInputTouched = null, ...rest }) => {
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [inputTouched, setInputTouched] = useState(false);
    const [inputValue, setInputValue] = useState(value);
    const { PopoverContainer, openPopover, closePopover, popoverProps, triggerProps, } = usePopover();
    // Handle outside Changes
    useEffect(() => {
        setInputTouched(value && inputTouched);
        setInputValue(value);
        !value?.length && setResults([]);
        if (value || (!value && !openOnInputFocus)) {
            !inlineResults && !persistPopoverOnNavigation && closePopover();
        }
    }, [value, persistPopoverOnNavigation]);
    useEffect(() => {
        if (highLightAndFocusOnInit) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, []);
    useEffect(() => {
        if (!inputValue && inputTouched) {
            onInputClearCallback && onInputClearCallback();
        }
    }, [inputValue]);
    useEffect(() => {
        if (isInputTouched !== null && !isInputTouched) {
            setResults([]);
            setInputTouched(false);
        }
    }, [isInputTouched]);
    const focusFirstListElement = () => {
        const listNodes = listRef?.current.getElementsByTagName('button');
        if (listNodes[0]) {
            listNodes[0]?.focus();
        }
    };
    const onFocusHandler = () => {
        if (!inlineResults && openOnInputFocus && !inputValue) {
            openPopover();
        }
        if (inputValue && highlightOnFocus) {
            inputRef.current.select();
            !inlineResults && openPopover();
        }
    };
    useEffect(() => {
        if (!inlineResults && inputValue?.length >= 0 && inputTouched) {
            openPopover();
        }
        else if (!inlineResults && !openOnInputFocus) {
            closePopover();
        }
    }, [inputValue]);
    function onChangeHandler(e) {
        setInputTouched(true);
        setInputValue(e.target.value);
        handleOnChange && handleOnChange(e.target.value);
        resultsHandler(e.target.value);
    }
    async function resultsHandler(value) {
        if (value.length < minimumInput) {
            return setResults([]);
        }
        setIsLoading(true);
        setIsError(false);
        try {
            const results = await resultsHandlerCallback(value);
            setResults(results);
            setIsLoading(false);
        }
        catch (e) {
            setIsError(true);
            setIsLoading(false);
        }
    }
    const handleItemOnClick = (item) => {
        setResults([]);
        setInputTouched(false);
        setInputValue(item.name);
        !inlineResults && setTimeout(() => closePopover());
        onItemClickCallback && onItemClickCallback(item);
    };
    const getNextSibling = function (elem, selector) {
        let sibling = elem.nextElementSibling;
        while (sibling) {
            if (sibling.matches(selector))
                return sibling;
            sibling = sibling.nextElementSibling;
        }
        return null;
    };
    const getPrevSibling = function (elem, selector) {
        let sibling = elem.previousElementSibling;
        while (sibling) {
            if (sibling.matches(selector))
                return sibling;
            sibling = sibling.previousElementSibling;
        }
        return null;
    };
    const handleNav = (keyPressed, index) => {
        const clickedItem = results[index];
        const focusedElement = document.activeElement;
        const previous = getPrevSibling(focusedElement, 'button');
        const next = getNextSibling(focusedElement, 'button');
        switch (keyPressed) {
            case DIRECTION_UP:
            case DIRECTION_LEFT:
                if (previous) {
                    previous.focus();
                }
                break;
            case DIRECTION_DOWN:
            case DIRECTION_RIGHT:
                if (next) {
                    next.focus();
                }
                break;
            case ENTER_KEY:
                handleItemOnClick({ name: clickedItem.name, ...clickedItem });
                break;
        }
    };
    const handleClear = () => {
        if (popoverProps.popoverVisible || inlineResults) {
            inputRef.current.focus();
        }
        setInputValue('');
        onInputClearCallback();
        resultsHandler('');
        setInputTouched(false);
    };
    closingPopoverCallback &&
        useEffect(() => {
            closingPopoverCallback(popoverProps.popoverVisible);
            if (!inlineResults && inputTouched && !popoverProps.popoverVisible) {
                setTimeout(() => closePopover());
            }
            setInputTouched(false);
            resultsHandler('');
            setResults([]);
        }, [popoverProps.popoverVisible]);
    const ResultsContainer = useMemo(() => ({ children }) => inlineResults ? (_jsx("div", { "data-testid": "inline", children: children })) : (_jsx(Popover, { ...popoverProps, boxConfig: { shadow: 16, padding: 0, radius: 4 }, placement: placement, offset: offset && { ...offset }, disableFlip: true, popoverWidth: popoverWidth, boundaryElement: boundaryElement, children: _jsx(InnerPopoverStyled, { popoverWidth: popoverWidth, children: children }) })), [popoverProps.popoverVisible, popoverWidth]);
    return (_jsxs(PopoverContainer, { block: true, children: [_jsx(Label, { htmlFor: name, children: label }), _jsxs(StyledContainer, { children: [_jsx(Input, { name: name, type: "text", value: inputValue, placeholder: translations.placeholder, onChange: onChangeHandler, onFocus: onFocusHandler, onKeyDown: (e) => elementKeyPressHandler(e, focusFirstListElement, null, [
                            DIRECTION_DOWN,
                        ]), autoComplete: "off", status: isError ? 'error' : null, ref: inputRef, icon: icon, ...triggerProps, ...rest }), inputValue && (_jsx(StyledClosebutton, { type: "button", onClick: handleClear, "aria-label": translations.clearButtonAriaLabel, "data-ga": "autocomplete-clear-search-input", children: _jsx(Close, {}) }))] }), _jsx(ResultsContainer, { children: _jsx(AutocompleteResults, { children: children, labels: translations, inlineResults: inlineResults, results: results, ref: listRef, handleItemOnClick: handleItemOnClick, isLoading: isLoading, isError: isError, setNoResult: setNoResult, handleNav: handleNav, inputValue: inputValue, inputTouched: inputTouched, minimumInput: minimumInput, handleClear: handleClear, useLegacyDestinationSelector: useLegacyDestinationSelector }) })] }));
};
//# sourceMappingURL=autocomplete.js.map